import { useEffect, useMemo, useState } from "react";
import { api, API, eur } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PackageCheck, FileText, FileSpreadsheet, Info } from "lucide-react";

const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);
const MONTHS = ["", "Januari", "Februari", "Maart", "April", "Mei", "Juni", "Juli", "Augustus", "September", "Oktober", "November", "December"];
const monthLabel = (ym) => { try { const [y, m] = ym.split("-"); return `${MONTHS[parseInt(m, 10)]} ${y}`; } catch { return ym; } };

export default function FactuurboekOpbrengsten() {
  const [from, setFrom] = useState(firstOfYear());
  const [to, setTo] = useState(today());
  const [carrier, setCarrier] = useState("");   // "" = Alle
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/factuurboek/opbrengsten/summary", { params: { from_date: from, to_date: to } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const tabs = useMemo(() => ["", ...(data?.available_carriers || [])], [data]);
  const visible = useMemo(() => {
    if (!data) return [];
    return carrier ? data.carriers.filter((c) => c.carrier === carrier) : data.carriers;
  }, [data, carrier]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ from_date: from, to_date: to, format: fmt });
      if (carrier) params.set("carrier", carrier);
      const res = await fetch(`${API}/factuurboek/opbrengsten/export?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("mislukt");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Opbrengsten${carrier ? "_" + carrier.replace(/\s/g, "") : ""}_${from}_${to}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Opbrengsten geëxporteerd (${fmt.toUpperCase()})`);
    } catch { toast.error("Kon rapport niet exporteren"); }
    finally { setBusy(""); }
  };

  const grandFor = (list) => list.reduce((a, c) => ({
    base: a.base + c.base, vat: a.vat + c.vat, total: a.total + c.total, count: a.count + c.count,
  }), { base: 0, vat: 0, total: 0, count: 0 });
  const grand = grandFor(visible);

  return (
    <div className="space-y-6" data-testid="opbrengsten-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-semibold tracking-widest text-emerald-700 uppercase">Diverse opbrengsten</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <PackageCheck className="h-7 w-7 text-emerald-600" /> Alle pakjesbedrijven uitgesplitst
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy === "pdf"} data-testid="opb-export-pdf" onClick={() => download("pdf")}>
            <FileText className="h-4 w-4 mr-1.5 text-red-600" /> {busy === "pdf" ? "Bezig…" : "Export PDF"}
          </Button>
          <Button variant="outline" disabled={busy === "xlsx"} data-testid="opb-export-xlsx" onClick={() => download("xlsx")}>
            <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" /> {busy === "xlsx" ? "Bezig…" : "Excel"}
          </Button>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="space-y-1.5"><Label>Van</Label><Input type="date" data-testid="opb-from" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Tot</Label><Input type="date" data-testid="opb-to" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="opb-refresh" onClick={load}>Tonen</Button>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 flex-wrap border-b pb-3">
        {tabs.map((t) => (
          <Button key={t || "all"} size="sm" variant={carrier === t ? "default" : "outline"}
            className={carrier === t ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            data-testid={`opb-tab-${t ? t.replace(/\s/g, "-") : "alle"}`} onClick={() => setCarrier(t)}>
            {t || "Alle"}
          </Button>
        ))}
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-emerald-50/60 border border-emerald-100 rounded-md p-3">
        <Info className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
        Bedragen zijn <b className="mx-1">exclusief btw</b> (de opbrengst). De btw is <b className="mx-1">verschuldigd</b> (output) — een schuld t.o.v. de Staat, geen aftrekbare btw.
      </div>

      {loading ? <div className="py-16 text-center text-muted-foreground">Laden…</div> :
        visible.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground flex flex-col items-center gap-3" data-testid="opb-empty">
            <FileText className="h-10 w-10 text-muted-foreground/40" />
            Nog geen verwerkte opbrengstdocumenten voor deze selectie.
          </CardContent></Card>
        ) : (
          <div className="space-y-6">
            {visible.map((c) => (
              <Card key={c.carrier} className="shadow-sm" data-testid={`opb-card-${c.carrier.replace(/\s/g, "-")}`}>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-lg text-emerald-800">{c.carrier}</CardTitle>
                  <span className="text-sm text-muted-foreground">{c.count} document(en)</span>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Maand</TableHead>
                      <TableHead className="text-right">Aantal</TableHead>
                      <TableHead className="text-right">Maatstaf (excl.)</TableHead>
                      <TableHead className="text-right">Btw (verschuldigd)</TableHead>
                      <TableHead className="text-right">Totaal</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {c.by_month.map((m) => (
                        <TableRow key={m.month} data-testid={`opb-${c.carrier.replace(/\s/g, "-")}-${m.month}`}>
                          <TableCell>{monthLabel(m.month)}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{m.count}</TableCell>
                          <TableCell className="text-right tabular-nums">{eur(m.base)}</TableCell>
                          <TableCell className="text-right tabular-nums">{eur(m.vat)}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium text-emerald-700">{eur(m.total)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/40 font-semibold">
                        <TableCell>Subtotaal {c.carrier}</TableCell>
                        <TableCell className="text-right">{c.count}</TableCell>
                        <TableCell className="text-right tabular-nums">{eur(c.base)}</TableCell>
                        <TableCell className="text-right tabular-nums">{eur(c.vat)}</TableCell>
                        <TableCell className="text-right tabular-nums">{eur(c.total)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}

            <Card className="border-emerald-200">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
                <span className="font-heading font-semibold text-emerald-900" data-testid="opb-grand-label">Algemeen totaal ({carrier || "alle bedrijven"})</span>
                <div className="flex gap-8 text-sm">
                  <div><div className="text-xs text-muted-foreground">Maatstaf (excl.)</div><div className="tabular-nums font-medium" data-testid="opb-grand-base">{eur(grand.base)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Btw verschuldigd</div><div className="tabular-nums font-medium" data-testid="opb-grand-vat">{eur(grand.vat)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Totaal</div><div className="tabular-nums font-semibold text-emerald-700" data-testid="opb-grand-total">{eur(grand.total)}</div></div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
    </div>
  );
}
